import { SDS_FONT_WEIGHT_REGULAR, SDS_FONT_FAMILY_PRIMARY, SDS_FONT_SIZE_BODY_SMALL, SDS_FONT_LINEHEIGHT_BODY_XSMALL, SDS_FONT_SIZE_BODY_MEDIUM, SDS_FONT_LINEHEIGHT_BODY_MEDIUM, SDS_FONT_SIZE_BODY_LARGE, SDS_FONT_LINEHEIGHT_BODY_LARGE, SDS_FONT_SIZE_HEADING_SMALL, SDS_FONT_LINEHEIGHT_HEADING_SMALL, SDS_FONT_SIZE_HEADING_MEDIUM, SDS_FONT_LINEHEIGHT_HEADING_MEDIUM } from '@s-group/design-system-tokens/web/tokens/font.es6.js';
import { SDS_MISC_ALPHA_MEDIUM } from '@s-group/design-system-tokens/web/tokens/misc.es6.js';
import { SDS_SIZE_BORDER_LARGE, SDS_SIZE_BORDER_SMALL, SDS_SIZE_XXXSMALL } from '@s-group/design-system-tokens/web/tokens/size.es6.js';
import { math, rem } from 'polished';
import { css } from 'styled-components';
import { indicatorSize } from './constants.js';
import { SDS } from '../common/namespace.js';

const fontMetrics = {
    // sizing: [font-size, line-height]
    xsmall: [SDS_FONT_SIZE_BODY_SMALL, SDS_FONT_LINEHEIGHT_BODY_XSMALL],
    small: [SDS_FONT_SIZE_BODY_MEDIUM, SDS_FONT_LINEHEIGHT_BODY_MEDIUM],
    medium: [SDS_FONT_SIZE_BODY_LARGE, SDS_FONT_LINEHEIGHT_BODY_LARGE],
    large: [SDS_FONT_SIZE_HEADING_SMALL, SDS_FONT_LINEHEIGHT_HEADING_SMALL],
    xlarge: [SDS_FONT_SIZE_HEADING_MEDIUM, SDS_FONT_LINEHEIGHT_HEADING_MEDIUM],
};
const font = ({ sizing }) => sizing &&
    [fontMetrics[sizing]]
        .map(([fontSize, lineHeight]) => `${SDS_FONT_WEIGHT_REGULAR} ${fontSize} / ${lineHeight} '${SDS_FONT_FAMILY_PRIMARY}'`)
        .join('');
const backgroundFill = ({ alternative, theme, }) => alternative
    ? theme[SDS].colors.SDS_BRAND_COLOR_BACKGROUND_WEAK_NEUTRAL
    : theme[SDS].colors.SDS_BRAND_COLOR_BACKGROUND_DEFAULT_NEUTRAL;
const borderColor = ({ error, theme }) => error
    ? theme[SDS].colors.SDS_BRAND_COLOR_BORDER_ERROR
    : theme[SDS].colors.SDS_BRAND_COLOR_BORDER_DEFAULT_NEUTRAL;
const focusOutlineCompensation = math(
// focus outline width + focus outline offset
`${SDS_SIZE_BORDER_LARGE} + ${SDS_SIZE_BORDER_SMALL}`);
const verticalMargin = ({ sizing }) => {
    if (sizing) {
        const [, lineHeight] = fontMetrics[sizing];
        const indicatorHeight = rem(indicatorSize[sizing]);
        return math(`(${lineHeight} - ${indicatorHeight}) / 2`);
    }
    return 0;
};
const compensateOutline = (margin) => math(`${margin} - ${focusOutlineCompensation}`);
// @media (forced-colors: active) sections define the appearance of the custom
// radio button indicator in the high contrast accessibility mode as the colors
// of SVG graphics are not automatically mapped.
// https://www.w3.org/TR/css-color-adjust-1/#forced
//
// The allowed values for system colors are defined in:
// https://www.w3.org/TR/css-color-4/#css-system-colors
//
// Testing forced colors:
// https://developer.chrome.com/docs/devtools/rendering/emulate-css/#emulate-css-media-feature-forced-colors
const CheckableBaseStyle = css `
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
    display: inline-flex;
    font: ${font};
    opacity: ${({ disabled }) => disabled && SDS_MISC_ALPHA_MEDIUM};

    /* Ensure that visually hidden input elements follow positioning of
       the component in scrolling containers. (See SDS-2125 for details.) */
    position: relative;

    input[type='checkbox'],
    input[type='radio'] {
        /* Visually hidden, accessible for screen readers */
        clip: rect(0 0 0 0);
        clip-path: inset(50%);
        height: 1px;
        overflow: hidden;
        position: absolute;
        white-space: nowrap;
        width: 1px;
    }

    .indicator {
        flex-shrink: 0;
        margin: ${props => [verticalMargin(props), SDS_SIZE_XXXSMALL, verticalMargin(props), 0]
    .map(compensateOutline)
    .join(' ')};

        .focus {
            fill: none;
        }

        .offset {
            fill: none;
        }

        .border {
            fill: ${borderColor};

            @media (forced-colors: active) {
                fill: buttonborder;
            }
        }

        .background {
            fill: ${backgroundFill};

            @media (forced-colors: active) {
                fill: field;
            }
        }

        .indeterminate,
        .toggle {
            fill: none;
        }
    }

    /* For radio buttons, there is no separate indeterminate state style,
     * as the indeterminate state where none of the options is selected
     * looks the same as the unselected state style of the individual
     * radio button.
     */
    input:checked + .indicator .background,
    input[type='checkbox']:indeterminate + .indicator .background {
        fill: ${({ error, theme }) => error
    ? theme[SDS].colors.SDS_BRAND_COLOR_BACKGROUND_ERROR
    : undefined};
    }

    input:checked:not(:indeterminate) + .indicator .toggle,
    input:indeterminate + .indicator .indeterminate {
        fill: ${({ error, theme }) => error
    ? theme[SDS].colors.SDS_BRAND_COLOR_ELEMENT_ERROR
    : theme[SDS].colors.SDS_BRAND_COLOR_ELEMENT_STRONG_PRIMARY};

        @media (forced-colors: active) {
            fill: selecteditem;
        }
    }

    input:focus-visible + .indicator {
        .focus {
            fill: ${({ theme }) => theme[SDS].colors.SDS_BRAND_COLOR_BORDER_FOCUS};

            @media (forced-colors: active) {
                fill: selecteditem;
            }
        }

        .offset {
            fill: ${({ theme }) => theme[SDS].colors.SDS_BRAND_COLOR_BORDER_INVERSE_NEUTRAL};
        }
    }

    input[type='checkbox']:checked + .indicator {
        .background {
            fill: ${({ theme, filled, error }) => error
    ? /* Default error style overrides filled */
        null
    : filled
        ? theme[SDS].colors
            .SDS_BRAND_COLOR_BACKGROUND_STRONG_PRIMARY
        : null};
        }

        .border {
            /* Fill border with background color to show only focus outline */
            fill: ${({ theme, filled, error }) => error
    ? /* Default error style overrides filled */
        null
    : filled
        ? theme[SDS].colors.SDS_BRAND_COLOR_BORDER_STRONG_PRIMARY
        : null};
        }
    }

    input[type='checkbox']:checked:not(:indeterminate) + .indicator .toggle,
    input[type='checkbox']:indeterminate + .indicator .indeterminate {
        fill: ${({ theme, filled, error }) => error
    ? /* Default error style overrides filled */
        null
    : filled
        ? theme[SDS].colors.SDS_BRAND_COLOR_ELEMENT_INVERSE_NEUTRAL
        : null};

        @media (forced-colors: active) {
            fill: selecteditem;
        }
    }
`;

export { CheckableBaseStyle };
//# sourceMappingURL=CheckableBase.styles.js.map
